import {
  categoryLabel,
  DAMAGE_CATEGORIES,
  DAMAGE_STATUSES,
  formatDamageNumber,
  isDamageCategory,
  isDamageStatus,
  parseDamageReference,
  statusLabel,
  viewLabel,
  type AircraftView,
} from '@fsv/core';
import type { Damage, PeriodDays, SearchQuery } from '../data/types';

export const EMPTY_QUERY: SearchQuery = {
  q: '',
  aircraft: null,
  category: null,
  statuses: [],
  view: null,
  periodDays: null,
  withPhoto: false,
};

export const MIN_QUERY_CHARS = 2;
const PERIODS: readonly PeriodDays[] = [30, 90, 365];
const VIEWS = Object.keys(viewLabel) as AircraftView[];

/** True when there is something to search for: enough text or at least one facet. */
export function isActiveQuery(q: SearchQuery): boolean {
  return (
    q.q.trim().length >= MIN_QUERY_CHARS ||
    q.aircraft !== null ||
    q.category !== null ||
    q.statuses.length > 0 ||
    q.view !== null ||
    q.periodDays !== null ||
    q.withPhoto
  );
}

/** URL → query. Unknown values are dropped rather than trusted. */
export function parseSearchParams(params: URLSearchParams): SearchQuery {
  const cat = params.get('cat');
  const view = params.get('view');
  const period = Number(params.get('zeit'));
  const ac = params.get('ac');
  return {
    q: params.get('q') ?? '',
    aircraft: ac && /^D-[A-Z0-9]{4}$/i.test(ac) ? ac.toUpperCase() : null,
    category: isDamageCategory(cat) ? cat : null,
    statuses: (params.get('status') ?? '').split(',').filter(isDamageStatus),
    view: view && (VIEWS as string[]).includes(view) ? (view as AircraftView) : null,
    periodDays: (PERIODS as number[]).includes(period) ? (period as PeriodDays) : null,
    withPhoto: params.get('foto') === '1',
  };
}

/** Query → URL, in a stable order and without empty parameters, so links stay short and shareable. */
export function toSearchParams(q: SearchQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.q.trim()) p.set('q', q.q.trim());
  if (q.aircraft) p.set('ac', q.aircraft);
  if (q.category) p.set('cat', q.category);
  if (q.statuses.length) p.set('status', DAMAGE_STATUSES.filter((s) => q.statuses.includes(s)).join(','));
  if (q.view) p.set('view', q.view);
  if (q.periodDays) p.set('zeit', String(q.periodDays));
  if (q.withPhoto) p.set('foto', '1');
  return p;
}

function fold(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss');
}

/**
 * The damage number a search text asks for exactly, or null for a text search.
 * `FSV-000042`, `fsv-42`, `000042`, `42`, `A00001`, `FSV-A00001` all count;
 * a reference with another club's code asks for a number that does not exist
 * here (-1), so it finds nothing instead of the same number in this club.
 */
export function exactNumberOf(text: string, tenantCode: string | undefined): number | null {
  const ref = parseDamageReference(text);
  if (!ref) return null;
  if (ref.code !== undefined && ref.code !== tenantCode) return -1;
  return ref.number;
}

/**
 * Relevance of one damage for the search text; 0 = no match.
 * Exact number and registration beat title, title beats description. Every
 * word must match somewhere (AND), each word may match the start of a word
 * ("Kratz" finds "Kratzer"), mirroring the API's FORMSOF + LIKE fallback.
 */
export function scoreDamage(damage: Damage, text: string, tenantCode?: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 1;
  const number = exactNumberOf(trimmed, tenantCode);
  if (number !== null) return damage.number === number ? 100 : 0;
  if (/^d-[a-z0-9]{4}$/i.test(trimmed)) return damage.registration.toUpperCase() === trimmed.toUpperCase() ? 50 : 0;

  const title = fold(damage.title);
  const description = fold(damage.description ?? '');
  const meta = fold(
    [damage.registration, damage.typeCode, ...damage.categories.map((c) => categoryLabel[c]), statusLabel[damage.status], viewLabel[damage.view], formatDamageNumber(damage.number)].join(' '),
  );
  let score = 0;
  for (const word of fold(trimmed).split(/\s+/).filter(Boolean)) {
    const startsWord = new RegExp(`(^|[^a-z0-9])${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
    if (startsWord.test(title)) score += 10;
    else if (title.includes(word)) score += 6;
    else if (startsWord.test(meta)) score += 4;
    else if (description.includes(word)) score += 3;
    else return 0;
  }
  return score;
}

/**
 * The whole search, used by the demo store; the real API does the same on the
 * server. `tenantCode` is the code of the club searched in.
 */
export function searchDamages(damages: readonly Damage[], query: SearchQuery, now: Date, tenantCode?: string): Damage[] {
  const since = query.periodDays ? now.getTime() - query.periodDays * 86_400_000 : null;
  const textActive = query.q.trim().length >= MIN_QUERY_CHARS;
  // 'verworfen' is noise in a normal search, but an exact number must find it.
  const exactNumber = exactNumberOf(query.q, tenantCode) !== null;
  const scored = damages
    .filter((d) => (query.aircraft ? d.registration === query.aircraft : true))
    .filter((d) => (query.category ? d.categories.includes(query.category) : true))
    .filter((d) => (query.statuses.length ? query.statuses.includes(d.status) : d.status !== 'verworfen' || exactNumber))
    .filter((d) => (query.view ? d.view === query.view : true))
    .filter((d) => (since !== null ? Date.parse(d.reportedAt) >= since : true))
    .filter((d) => (query.withPhoto ? d.imageCount > 0 : true))
    .map((d) => ({ d, score: textActive ? scoreDamage(d, query.q, tenantCode) : 1 }))
    .filter((x) => x.score > 0);
  scored.sort((a, b) => b.score - a.score || Date.parse(b.d.reportedAt) - Date.parse(a.d.reportedAt));
  return scored.map((x) => x.d);
}

export const ALL_CATEGORIES = DAMAGE_CATEGORIES;
